import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CURATION_PASSCODE_KEY, NewViewerRule, ViewerRule, ViewerSignIn, ViewerSite, ViewersApi } from '../utils/clipApi';

// The board's Viewers panel (SIL-1595): who signed in to the shows that ask for a company email
// (pll.silvr.shop), and who may. It reads and writes the API's /viewers routes with the curation passcode
// the Curate page already keeps in sessionStorage for the tab, and asks for it once when it is not there.
//   Sign-ins: every attempt, newest first, with its outcome. A row offers Block (that address, on every
//             site) and Block domain; a refused row also offers Allow (an exception for that address on
//             the site it tried).
//   Rules:    the allowed domains and exceptions, and the blocks, each removable; a form adds one. A block
//             beats an allow; a domain rule covers its subdomains.
// A change is live on the next sign-in, and a block ends an existing sign-in on its next media request
// (the API checks the pass against the block list, cached 30 s).

/** The scope that means "every site" in the add-rule form; the API wants site: null for it. */
const EVERYWHERE = '*';

const OUTCOME: Record<ViewerSignIn['outcome'], { label: string; className: string }> = {
  ok: { label: 'Signed in', className: 'bg-emerald-50 text-emerald-700' },
  refused: { label: 'Refused', className: 'bg-amber-50 text-amber-800' },
  blocked: { label: 'Blocked', className: 'bg-red-50 text-red-700' },
  code_sent: { label: 'Code sent', className: 'bg-sky-50 text-sky-700' },
  code_failed: { label: 'Wrong code', className: 'bg-orange-50 text-orange-800' },
};

const readPasscode = (): string => {
  try {
    return sessionStorage.getItem(CURATION_PASSCODE_KEY) || '';
  } catch {
    return '';
  }
};

const keepPasscode = (value: string) => {
  try {
    if (value) sessionStorage.setItem(CURATION_PASSCODE_KEY, value);
    else sessionStorage.removeItem(CURATION_PASSCODE_KEY);
  } catch {
    /* private mode */
  }
};

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

const ttlLabel = (seconds: number | null) => {
  if (!seconds) return '12 h (API default)';
  const hours = seconds / 3600;
  return hours >= 48 ? `${Math.round(hours / 24)} days` : `${Math.round(hours)} h`;
};

const isAddress = (pattern: string) => pattern.includes('@');

const RuleRow: React.FC<{ rule: ViewerRule; busy: boolean; onRemove: () => void }> = ({ rule, busy, onRemove }) => (
  <li className="flex items-start justify-between gap-3 py-2">
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">
        {isAddress(rule.pattern) ? rule.pattern : `@${rule.pattern}`}
        <span className="ml-2 text-[11px] font-normal text-gray-500">{isAddress(rule.pattern) ? 'address' : 'domain'}</span>
      </p>
      <p className="text-[11px] text-gray-500">
        {rule.site ?? 'every site'}
        {rule.note ? ` · ${rule.note}` : ''}
        {rule.created_at ? ` · ${when(rule.created_at)}` : ''}
      </p>
    </div>
    <button type="button" className="viewers__btn" disabled={busy} onClick={onRemove} title="Remove this rule">
      {rule.action === 'block' ? 'Unblock' : 'Remove'}
    </button>
  </li>
);

/** The panel: the passcode form until the API accepts one, then the log and the rules. */
const ViewersPanel: React.FC = () => {
  const [passcode, setPasscode] = useState<string>(readPasscode);
  const [typed, setTyped] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const api = useMemo(() => (passcode ? new ViewersApi(passcode) : null), [passcode]);

  const [sites, setSites] = useState<ViewerSite[]>([]);
  /** '' = every site. */
  const [site, setSite] = useState('');
  const [signins, setSignins] = useState<ViewerSignIn[]>([]);
  const [rules, setRules] = useState<ViewerRule[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState<{ action: 'allow' | 'block'; pattern: string; note: string; scope: string }>({
    action: 'allow',
    pattern: '',
    note: '',
    scope: '',
  });

  const fail = useCallback((err: Error) => {
    if (err.message === 'Wrong passcode' || /api key/i.test(err.message)) {
      // like the Curate page: a passcode the API refuses is forgotten, and the form comes back
      setLoginError(err.message);
      setPasscode('');
      keepPasscode('');
      setLoaded(false);
    } else {
      setMessage(err.message);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!api) return;
    try {
      const [s, si, r] = await Promise.all([api.listSites(), api.listSignIns(site || undefined), api.listRules(site || undefined)]);
      setSites(s.sites);
      setSignins(si.signins);
      setRules(r.rules);
      setLoaded(true);
      setMessage(null);
      setLoginError(null);
      keepPasscode(passcode);
    } catch (err) {
      fail(err as Error);
    }
  }, [api, site, passcode, fail]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const change = async (work: () => Promise<unknown>) => {
    if (!api || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      await work();
      await refresh();
    } catch (err) {
      fail(err as Error);
    } finally {
      setBusy(false);
    }
  };

  const addRule = (rule: NewViewerRule) => change(() => api!.addRule(rule));
  const removeRule = (id: string) => change(() => api!.removeRule(id));

  const blockedPatterns = useMemo(() => new Set(rules.filter((r) => r.action === 'block').map((r) => r.pattern)), [rules]);
  const allows = rules.filter((r) => r.action === 'allow');
  const blocks = rules.filter((r) => r.action === 'block');
  // the add form's scope: what the user picked, else the site being looked at, else the first site
  const scope = form.scope || site || sites[0]?.host || EVERYWHERE;

  const submitRule = (event: React.FormEvent) => {
    event.preventDefault();
    const pattern = form.pattern.trim();
    if (!pattern) return;
    void addRule({ action: form.action, pattern, site: scope === EVERYWHERE ? null : scope, note: form.note.trim() || null }).then(() =>
      setForm((prev) => ({ ...prev, pattern: '', note: '' })),
    );
  };

  return (
    <section className="viewers silvr-ui" aria-labelledby="viewers-title">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Workshop</p>
          <h2 id="viewers-title" className="text-base font-semibold leading-tight">
            Viewers
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Who signed in to the sites that ask for an email (the shows, and this board), and who may. A block is live on the next request.
          </p>
        </div>
        {api && loaded && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500" htmlFor="viewers-site">
              Site
            </label>
            <select id="viewers-site" className="viewers__input" value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="">Every site</option>
              {sites.map((s) => (
                <option key={s.host} value={s.host}>
                  {s.host}
                </option>
              ))}
            </select>
            <button type="button" className="viewers__btn" disabled={busy} onClick={() => void refresh()}>
              Refresh
            </button>
          </div>
        )}
      </header>

      {!api && (
        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setLoginError(null);
            setPasscode(typed.trim());
          }}
        >
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="viewers-passcode">
              Curation passcode
            </label>
            <input
              id="viewers-passcode"
              type="password"
              autoComplete="off"
              className="viewers__input mt-1"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              required
            />
          </div>
          <button type="submit" className="viewers__btn is-dark">
            Open
          </button>
          {loginError && (
            <p role="alert" className="w-full text-sm font-medium text-red-600">
              {loginError}
            </p>
          )}
        </form>
      )}

      {api && !loaded && !message && <p className="mt-4 text-sm text-gray-500">Loading…</p>}
      {message && (
        <p role="alert" className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
          {message}
        </p>
      )}

      {api && loaded && (
        <div className="viewers__grid mt-4">
          <div>
            <h3 className="text-sm font-semibold">Recent sign-ins</h3>
            {sites.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-500">
                {sites.map((s) => `${s.host}: ${s.mode === 'email' ? `email${s.email_code ? ' + emailed code' : ''}, a sign-in lasts ${ttlLabel(s.cookie_ttl_seconds)}` : 'passcode'}`).join(' · ')}
              </p>
            )}
            {signins.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Nobody has signed in yet.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Email</th>
                      <th>Outcome</th>
                      {!site && <th>Site</th>}
                      <th>IP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {signins.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap text-gray-500">{when(row.created_at)}</td>
                        <td className="font-medium">{row.email}</td>
                        <td>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${OUTCOME[row.outcome]?.className ?? 'bg-gray-100 text-gray-700'}`}>
                            {OUTCOME[row.outcome]?.label ?? row.outcome}
                          </span>
                        </td>
                        {!site && <td className="text-gray-500">{row.site}</td>}
                        <td className="whitespace-nowrap text-gray-500">{row.ip ?? ''}</td>
                        <td>
                          <div className="flex flex-wrap justify-end gap-1">
                            {row.outcome === 'refused' && (
                              <button
                                type="button"
                                className="viewers__btn"
                                disabled={busy}
                                title={`Let ${row.email} in on ${row.site} (an exception for this one address)`}
                                onClick={() => void addRule({ action: 'allow', pattern: row.email, site: row.site, note: 'exception, from the sign-in log' })}
                              >
                                Allow
                              </button>
                            )}
                            {!blockedPatterns.has(row.email) && !blockedPatterns.has(row.domain) && (
                              <>
                                <button
                                  type="button"
                                  className="viewers__btn is-danger"
                                  disabled={busy}
                                  title={`Block ${row.email} on every site`}
                                  onClick={() => void addRule({ action: 'block', pattern: row.email, site: null, note: 'from the sign-in log' })}
                                >
                                  Block
                                </button>
                                <button
                                  type="button"
                                  className="viewers__btn is-danger"
                                  disabled={busy}
                                  title={`Block everyone at @${row.domain} on every site`}
                                  onClick={() => void addRule({ action: 'block', pattern: row.domain, site: null, note: 'from the sign-in log' })}
                                >
                                  Block domain
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold">Who may sign in{site ? ` on ${site}` : ''}</h3>
            <p className="mt-1 text-[11px] text-gray-500">A domain covers its subdomains. A block beats any allow.</p>
            <h4 className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Allowed</h4>
            {allows.length === 0 ? (
              <p className="mt-1 text-sm text-gray-500">Nothing allowed{site ? ' here' : ''} yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {allows.map((rule) => (
                  <RuleRow key={rule.id} rule={rule} busy={busy} onRemove={() => void removeRule(rule.id)} />
                ))}
              </ul>
            )}
            <h4 className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Blocked</h4>
            {blocks.length === 0 ? (
              <p className="mt-1 text-sm text-gray-500">Nobody is blocked.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {blocks.map((rule) => (
                  <RuleRow key={rule.id} rule={rule} busy={busy} onRemove={() => void removeRule(rule.id)} />
                ))}
              </ul>
            )}

            <form className="mt-4 grid gap-2 rounded-xl bg-gray-50 p-3" onSubmit={submitRule}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Add a rule</p>
              <div className="flex flex-wrap gap-2">
                <select
                  className="viewers__input"
                  aria-label="Allow or block"
                  value={form.action}
                  onChange={(e) => setForm((prev) => ({ ...prev, action: e.target.value === 'block' ? 'block' : 'allow' }))}
                >
                  <option value="allow">Allow</option>
                  <option value="block">Block</option>
                </select>
                <input
                  className="viewers__input min-w-0 flex-1"
                  aria-label="Email address or domain"
                  placeholder="anna@wbd.com or wbd.com"
                  value={form.pattern}
                  onChange={(e) => setForm((prev) => ({ ...prev, pattern: e.target.value }))}
                  required
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <select className="viewers__input" aria-label="On which site" value={scope} onChange={(e) => setForm((prev) => ({ ...prev, scope: e.target.value }))}>
                  {sites.map((s) => (
                    <option key={s.host} value={s.host}>
                      on {s.host}
                    </option>
                  ))}
                  <option value={EVERYWHERE}>on every site</option>
                </select>
                <input
                  className="viewers__input min-w-0 flex-1"
                  aria-label="Note"
                  placeholder="Note (optional)"
                  value={form.note}
                  onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                />
                <button type="submit" className="viewers__btn is-dark" disabled={busy || !form.pattern.trim()}>
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ViewersPanel;
